/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * The input component to validate phone by mask
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @author Alexander Golshtein
 * @version 1.23.0
 * @since 1.2.0
 */

import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, RefObject } from 'react';

import InputMask from 'react-input-mask';

import type ICommonTranslation from '@modules/internationalization/types/common';

import { Input } from '../index';
import { EInputKind } from '../Input/Input';
import type { IInputProps } from '../Input/Input';

interface IPhoneInput {
    isAllowEmpty?: boolean;
    initialValue?: string;
    isValid?: boolean;
    label?: string;
    onPhoneInputBlur?: (value: string) => void;
    onPhoneInputChange?: (value: string) => void;
    onPhoneInputValidate?: (value: boolean) => void;
    phoneInputValue: string;
    phoneMask: string;
    phoneMaskChar?: string;
    phonePlaceholder?: string;
    translation: ICommonTranslation;
}

export type TPhoneInputProps = IPhoneInput & IInputProps;

export const PhoneInput = (props: TPhoneInputProps) => {
    const PHONE_LENGTH = 10;
    const phoneRegExp = new RegExp('^(9)[0-9]{9}$');
    const { isAllowEmpty, isValid: propsIsValid, phonePlaceholder, onPhoneInputBlur, onPhoneInputChange,
        onPhoneInputValidate, phoneMask, phoneMaskChar, phoneInputValue, translation, ...inputProps } = props;
    const [isValid, setValid] = useState(true);
    const inputRef: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

    const handleValidate = (value: string) => {
        const isValid = (isAllowEmpty ? value.length === 0 : false) ||
            (value.length === PHONE_LENGTH && phoneRegExp.test(value));
        setValid(isValid);
        if (onPhoneInputValidate) onPhoneInputValidate(isValid);
    };

    useEffect(() => {
        if (propsIsValid !== undefined) setValid(propsIsValid);
    }, [propsIsValid]);

    useEffect(() => {
        if (phoneInputValue) handleValidate(phoneInputValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.replace(/(\+7)|\D/g, '');
        if (!isValid) handleValidate(value);
        if (onPhoneInputChange) onPhoneInputChange(value);
    };

    const handleBlur = () => {
        handleValidate(phoneInputValue);
        if (onPhoneInputBlur) onPhoneInputBlur(phoneInputValue);
    };

    return (
        <Input
            dataName={props.dataName}
            hintMessage={isValid ? undefined : props.translation.phoneInput.hint}
            kind={isValid ? undefined : EInputKind.error}
            label={props.label}
            required={!isAllowEmpty}
        >
            <InputMask
                {...inputProps}
                data-name='input-phone-mask'
                inputRef={inputRef}
                mask={phoneMask}
                maskChar={phoneMaskChar ? phoneMaskChar : ''}
                placeholder={phonePlaceholder ? phonePlaceholder : translation.phoneInput.placeholder}
                value={phoneInputValue}
                onBlur={handleBlur}
                onChange={handleChange}
            />
        </Input>
    )
};
